import { css } from '@emotion/react'
import { useQuery } from '@tanstack/react-query'
import type { ReactElement } from 'react'

import type { EndorsementChange, GetChangesInput } from '@orus.eu/backend'
import type { FeatureBuilderMapWithValidatedState } from '@orus.eu/dimensions'
import {
  colorTokens,
  featureBuilderMap,
  Spinner,
  spacing,
  Text,
  type UiContext,
  useLanguage,
  useTranslate,
  useUiContext,
} from '@orus.eu/pharaoh'

import { useTRPC } from '../../../client.ts'
import { TableCellHeader } from '../../molecules/table-elements/table-cell-header.tsx'
import { BackofficeTable } from '../backoffice-table.tsx'
import { GenericProblemMessage } from '../generic-problem-message.tsx'

import { ChangesRow } from './contract-change-recap-rows.tsx'

type ContractChangeRecapProps = GetChangesInput

export function ContractChangeRecap({
  amendmentId,
  subscriptionActionType,
  status,
  renewedPeriodIndex,
}: ContractChangeRecapProps): ReactElement {
  const trpc = useTRPC()
  const language = useLanguage()
  const translate = useTranslate()
  const uiContext = useUiContext()
  const {
    data: changesData,
    isLoading: isChangesLoading,
    isError: isChangesError,
  } = useQuery(
    trpc.endorsement.getChangesV2.queryOptions({
      amendmentId,
      subscriptionActionType,
      language,
      status,
      renewedPeriodIndex,
    }),
  )
  const changes = changesData?.changes ?? []
  const hasUnvalidatedUpdates = changesData?.hasUnvalidatedUpdates
  const {
    data: paymentRecapInputs,
    isLoading: isPaymentRecapInputsLoading,
    isError: isPaymentRecapInputsError,
  } = useQuery(
    trpc.endorsement.getPaymentRecapInputs.queryOptions({
      endorsementId: amendmentId,
      subscriptionActionType,
    }),
  )

  const isAnyLoading = isChangesLoading || isPaymentRecapInputsLoading

  if (isAnyLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 80vh;
          width: 100%;
        `}
      >
        <Spinner size="40" />
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing[70]};
        width: 100%;
      `}
    >
      <div>
        <Text
          variant="subtitle"
          css={css`
            margin-bottom: ${spacing[50]};
          `}
        >
          {translate('contract_payment_details')}
        </Text>
        {isPaymentRecapInputsError || paymentRecapInputs?.type !== 'success' ? (
          <GenericProblemMessage
            title={translate('error_occurred')}
            principalMessage={translate('contract_changes_fetch_error')}
          />
        ) : (
          paymentRecapInputs?.type === 'success' && (
            <PaymentRecapTable
              paymentRecapInputs={paymentRecapInputs.output}
              uiContext={uiContext}
              subscriptionActionType={subscriptionActionType}
              hasUnvalidatedUpdates={paymentRecapInputs.output.hasUnvalidatedUpdates}
            />
          )
        )}
      </div>

      <div>
        <Text
          variant="subtitle"
          css={css`
            margin-bottom: ${spacing[50]};
          `}
        >
          {translate('contract_changes')}
        </Text>
        {isChangesError ? (
          <GenericProblemMessage
            title={translate('error_occurred')}
            principalMessage={translate('contract_changes_fetch_error')}
          />
        ) : changes?.length === 0 || !changes ? (
          <div>{translate('no_changes')}</div>
        ) : (
          <Table
            changes={changes}
            uiContext={uiContext}
            subscriptionActionType={subscriptionActionType}
            {...(hasUnvalidatedUpdates !== undefined && { hasUnvalidatedUpdates })}
          />
        )}
      </div>

      {/* TODO: @gaeduron Wire-up conditions requises */}
      {/* <div>
        <Text variant="subtitle" css={css`margin-bottom: ${spacing[50]};`}>
          Conditions requises
        </Text>
        <PersistentNotification variant="info">
          Une attestation SIREN sera requise pour valider votre avenant.
        </PersistentNotification>
      </div> */}
    </div>
  )
}

function PaymentRecapTable({
  paymentRecapInputs,
  uiContext,
  subscriptionActionType,
  hasUnvalidatedUpdates,
}: {
  paymentRecapInputs: FeatureBuilderMapWithValidatedState
  uiContext: UiContext
  subscriptionActionType: 'renewal' | 'endorsement'
  hasUnvalidatedUpdates?: boolean | undefined
}) {
  const translate = useTranslate()
  const isRenewal = subscriptionActionType === 'renewal'
  const showValidatedColumn = isRenewal && hasUnvalidatedUpdates === true
  const columnWidth = showValidatedColumn ? '25%' : '33.33%'

  const rows = [
    {
      ...featureBuilderMap.InsuranceProduct({
        before: paymentRecapInputs.InsuranceProduct.before!,
        after: paymentRecapInputs.InsuranceProduct.after!,
      }),
      contentValidated:
        showValidatedColumn && paymentRecapInputs.InsuranceProduct.validated
          ? featureBuilderMap.InsuranceProduct({
              before: paymentRecapInputs.InsuranceProduct.validated,
              after: paymentRecapInputs.InsuranceProduct.validated,
            }).contentAfter
          : undefined,
    },
    {
      ...featureBuilderMap.PaymentRecurrence({
        before: paymentRecapInputs.PaymentRecurrence.before!,
        after: paymentRecapInputs.PaymentRecurrence.after!,
      }),
      contentValidated:
        showValidatedColumn && paymentRecapInputs.PaymentRecurrence.validated
          ? featureBuilderMap.PaymentRecurrence({
              before: paymentRecapInputs.PaymentRecurrence.validated,
              after: paymentRecapInputs.PaymentRecurrence.validated,
            }).contentAfter
          : undefined,
    },
    {
      ...featureBuilderMap.Commitment({
        before: paymentRecapInputs.Commitment.before!,
        after: paymentRecapInputs.Commitment.after!,
      }),
      contentValidated:
        showValidatedColumn && paymentRecapInputs.Commitment.validated
          ? featureBuilderMap.Commitment({
              before: paymentRecapInputs.Commitment.validated,
              after: paymentRecapInputs.Commitment.validated,
            }).contentAfter
          : undefined,
    },
    {
      ...featureBuilderMap.Taxes({
        before: paymentRecapInputs.Taxes.before!,
        after: paymentRecapInputs.Taxes.after!,
      }),
      contentValidated:
        showValidatedColumn && paymentRecapInputs.Taxes.validated
          ? featureBuilderMap.Taxes({
              before: paymentRecapInputs.Taxes.validated,
              after: paymentRecapInputs.Taxes.validated,
            }).contentAfter
          : undefined,
    },
  ]
  return (
    <BackofficeTable
      columns={
        <>
          <col style={{ width: columnWidth }} />
          <col style={{ width: columnWidth }} />
          {showValidatedColumn && <col style={{ width: columnWidth }} />}
          <col style={{ width: columnWidth }} />
        </>
      }
      headers={
        <>
          <TableCellHeader>{translate('variable')}</TableCellHeader>
          <TableCellHeader>{isRenewal ? translate('previous_version') : translate('before')}</TableCellHeader>
          {showValidatedColumn && <TableCellHeader>{translate('validated_version')}</TableCellHeader>}
          <TableCellHeader>{isRenewal ? translate('current_version') : translate('after')}</TableCellHeader>
        </>
      }
      rows={rows.map((row) => (
        <ChangesRow
          key={row.title}
          variable={row.title}
          beforeChangeFormattedValue={row.contentBefore}
          afterChangeFormattedValue={row.contentAfter}
          validatedChangeFormattedValue={row.contentValidated}
          beforeChangeColor={
            row.areBeforeAfterEqual ? colorTokens['color-text-base-main'] : colorTokens['color-text-base-disable']
          }
          showValidatedColumn={showValidatedColumn}
          uiContext={uiContext}
        />
      ))}
    />
  )
}

function Table({
  changes,
  uiContext,
  subscriptionActionType,
  hasUnvalidatedUpdates,
}: {
  changes: EndorsementChange[]
  uiContext: UiContext
  subscriptionActionType: 'renewal' | 'endorsement'
  hasUnvalidatedUpdates?: boolean
}) {
  const translate = useTranslate()
  const isRenewal = subscriptionActionType === 'renewal'
  const showValidatedColumn = isRenewal && hasUnvalidatedUpdates === true
  const columnWidth = showValidatedColumn ? '25%' : '33.33%'
  return (
    <BackofficeTable
      columns={
        <>
          <col style={{ width: columnWidth }} />
          <col style={{ width: columnWidth }} />
          {showValidatedColumn && <col style={{ width: columnWidth }} />}
          <col style={{ width: columnWidth }} />
        </>
      }
      headers={
        <>
          <TableCellHeader>{translate('variable')}</TableCellHeader>
          <TableCellHeader>{isRenewal ? translate('previous_version') : translate('before')}</TableCellHeader>
          {showValidatedColumn && <TableCellHeader>{translate('validated_version')}</TableCellHeader>}
          <TableCellHeader>{isRenewal ? translate('current_version') : translate('after')}</TableCellHeader>
        </>
      }
      rows={changes.map((change) => (
        <ChangesRow key={change.variable} {...change} uiContext={uiContext} showValidatedColumn={showValidatedColumn} />
      ))}
    />
  )
}
